import { useStorage } from '@vueuse/core'
import { computed } from 'vue'
import {
  classSVG,
  CSSStyleColoring,
  CSSStyleTextOpacity,
  CSSVariableNetworkState,
  CSSVariableTextOpacityNetworkState,
} from '~/composables/coloring/helpers'
import { CSSStyle } from '~/types'
import { ColoringContextEnum, SVGLineStyleEnum } from '~/composables/coloring/constants'
import { injectAppSettings } from '~/composables/app-settings/provideAppSettings'
import { SVGColorItem } from '~/composables/svg/useSVGColoring'
import type { SwitchingsRepository } from '~/composables/repositories/switchingsRepository'
import { FilterTypeEnum, generateFilterKey } from '~/composables/useNetworkSVG'
import { EquipmentNetworkStateEnum } from '~/lib/equipments'
import type { EquipmentNetworkState, EquipmentWithColorInfo } from '~/lib/equipments'

export type NetworkColoringStorage = {
  createdAt: number
  colorMode: NetworkStateColoringEnum
}

export enum NetworkStateColoringEnum {
  COLORED = 'colored',
  HIDDEN = 'hidden',
}

export type SwitchingStateColoring = ReturnType<typeof useSVGNetworkStateColoring>
export type EquipmentsByState = Record<EquipmentNetworkStateEnum, EquipmentWithColorInfo[]>

/**
 * Handles the coloring based on the network state.
 */
export default function useSVGNetworkStateColoring(activityId: number, switchingsRepository: SwitchingsRepository) {
  const { settings } = injectAppSettings()
  const inStorage = useStorage<NetworkColoringStorage>(`${activityId}.color-mode-network`, {
    createdAt: new Date().getTime(),
    colorMode: NetworkStateColoringEnum.HIDDEN,
  })

  const colorMode = computed({
    get: () => inStorage.value.colorMode,
    set: (value) => (inStorage.value.colorMode = value),
  })

  function styleSVG(item: EquipmentNetworkState): CSSStyle {
    let style: CSSStyle = {}

    if (colorMode.value === NetworkStateColoringEnum.COLORED) {
      const cssVar = CSSVariableNetworkState(item.state)
      style = CSSStyleColoring(ColoringContextEnum.SVG, cssVar)

      // Add text opacity
      const cssVarTextOpacity = CSSVariableTextOpacityNetworkState(item.state)
      const textOpacityStyle = CSSStyleTextOpacity(cssVarTextOpacity)
      style = { ...style, ...textOpacityStyle }
    } else if (colorMode.value === NetworkStateColoringEnum.HIDDEN) {
      if (item.state === EquipmentNetworkStateEnum.ONLINE) {
        return style
      }
      if (item.state === EquipmentNetworkStateEnum.OFFLINE) {
        // style.display = 'none'
        style.opacity = '0.05'
      }
    }
    return style
  }

  function getSVGLineStyle(state: EquipmentNetworkStateEnum) {
    let lineStyle: SVGLineStyleEnum = SVGLineStyleEnum.FILL
    if (colorMode.value == NetworkStateColoringEnum.COLORED) {
      const setting = settings.value.design.coloringSVGNetworkState[state]
      lineStyle = setting.lineStyle
    }
    return lineStyle
  }

  /**
   * Transform EquipmentNetworkState into SVGColorItem
   */
  function equipmentNetworkState2SVGColorItem(item: EquipmentNetworkState): SVGColorItem {
    return {
      equipment_name: item.equipment.name,
      filterKey: generateFilterKey(FilterTypeEnum.network_state, item.state),
      style: () => styleSVG(item),
      cssClass: () => {
        const lineStyle = getSVGLineStyle(item.state)
        if (inStorage.value.colorMode === NetworkStateColoringEnum.COLORED) {
          return classSVG(lineStyle)
        }
        return []
      },
    }
  }

  function networkState2ColorItems(equipmentNetworkStates: EquipmentNetworkState[]): SVGColorItem[] {
    return equipmentNetworkStates.map(equipmentNetworkState2SVGColorItem)
  }

  function getEquipmentsByState(states: EquipmentNetworkState[]): EquipmentsByState {
    const equipmentsByState: EquipmentsByState = {
      online: [],
      offline: [],
    }
    for (const equipmentState of states) {
      if (equipmentState.state === EquipmentNetworkStateEnum.ONLINE) {
        equipmentsByState.online.push(networkState2ColorInfo(equipmentState))
      }
      if (equipmentState.state === EquipmentNetworkStateEnum.OFFLINE) {
        equipmentsByState.offline.push(networkState2ColorInfo(equipmentState))
      }
    }
    return equipmentsByState
  }

  function networkState2ColorInfo(state: EquipmentNetworkState): EquipmentWithColorInfo {
    // Get the color information using the same logic as networkState2ColorItems
    const colorItem = equipmentNetworkState2SVGColorItem(state)

    // Get project information - simplified since project_id might not exist
    const projects = switchingsRepository.getEquipmentProjects(state.equipment.id)

    return {
      equipment: state.equipment,
      cssClass: colorItem.cssClass(),
      style: colorItem.style(),
      projects: projects,
    }
  }

  return {
    inStorage,
    // styleSVG: styleSVG,
    // classSVG: _classSVG,
    // getSVGLineStyle: getSVGLineStyle,

    getEquipmentsByState: getEquipmentsByState,
    networkState2ColorItems: networkState2ColorItems, // for SVG
    networkState2ColorInfo: networkState2ColorInfo, // for report table & SVG Table
  }
}
